// Текстовые описания событий движка — для ленты при передаче телефона, тикера и журнала.
import { h, Fragment } from 'preact';
import type { ComponentChildren, JSX } from 'preact';
import { render as toString } from 'preact-render-to-string';
import type { CardId, Game, GameEvent, PlayerId } from '../types.ts';
import { cardOrNull, DB, ddkPenalty } from '../engine/state.ts';
import { CardChipV, ChipIc } from './cards.ts';
import type { SoundName } from './sound.ts';

export interface EventDesc {
  /** значок строки: эмодзи или компонент (значок чипсины) */
  icon: ComponentChildren;
  text: string;
  cls?: string;
  minor?: boolean;
  /** карта, о которой речь — для плашки */
  cid?: CardId | null;
  /** звук при показе в ленте (кроме «minor» без звука) */
  sound?: SoundName;
}

/** Имена игроков и карты в тексте события — маркеры; в html имя — цветной бейдж, карта — плашка (textHtml),
 *  в чистом тексте — имя и «название» (textPlain) */
const MARK = /\u0001(\d+)\u0002|\u0003([^\u0004]+)\u0004/g;
const mark = (pid: PlayerId): string => `\u0001${pid}\u0002`;
const cardMark = (cid: CardId): string => `\u0003${cid}\u0004`;
const playerName = (G: Game, pid: PlayerId): string => G.players[pid]?.name ?? `Игрок ${pid + 1}`;

/** В тексте есть плашка карты (дублирующую плашку в конце строки не показываем) */
export const hasCardMark = (text: string): boolean => text.includes('\u0003');

/** Текст события компонентом: имена игроков — бейджи своего цвета, карты — плашки, остальное — текст */
export function TextV({ G, text }: { G: Game; text: string }): JSX.Element {
  const parts: ComponentChildren[] = [];
  let last = 0;
  for (const m of text.matchAll(MARK)) {
    parts.push(text.slice(last, m.index));
    if (m[1] != null) {
      const pid = Number(m[1]);
      parts.push(h('span', { class: `pname pc${pid % 5}` }, playerName(G, pid)));
    } else if (m[2] != null) parts.push(h(CardChipV, { cid: m[2] }));
    last = (m.index ?? 0) + m[0].length;
  }
  parts.push(text.slice(last));
  return h(Fragment, null, parts);
}
/** То же строкой (уведомления, тикер в app.ts) */
export function textHtml(G: Game, text: string): string {
  return toString(h(TextV, { G, text }));
}

/** Текст события без маркеров (для уведомлений и текстового журнала) */
export function textPlain(G: Game, text: string): string {
  return text.replace(MARK, (_m, pid: string | undefined, cid: string | undefined) =>
    pid != null ? playerName(G, Number(pid)) : `«${cardOrNull(cid)?.name ?? '?'}»`);
}

export function describeEvent(_G: Game, e: GameEvent, me: PlayerId | null = null): EventDesc | null {
  const nm = (pid: PlayerId | null | undefined): string => (pid == null ? '' : mark(pid));
  const cn = (cid: CardId | null | undefined): string => (cid && cardOrNull(cid) ? cardMark(cid) : '');
  switch (e.type) {
    case 'turn': return { icon: '▶', text: `Ход: ${nm(e.pid)}`, cls: 'ev-turn' };
    case 'play': return { icon: '🎴', text: `${nm(e.pid)} играет ${cn(e.cid)}${e.from != null ? ` (с колоды ${nm(e.from)})` : ''}`, cid: e.cid };
    case 'activate': return e.cid ? { icon: '☀', text: `${nm(e.pid)} применяет ${cn(e.cid)}`, cid: e.cid } : { icon: '☀', text: `${nm(e.pid)} применяет жетон` };
    case 'buy': return { icon: '🛒', text: `${nm(e.pid)} покупает за ${e.cost}${e.chips ? ` (${e.chips} чипсинами)` : ''}`, cid: e.cid, cls: 'ev-buy' };
    case 'power': return null;
    case 'chips': return e.n > 0 ? { icon: h(ChipIc, null), text: `${nm(e.pid)} +${e.n} чипсин${e.src ? ` — ${e.src}` : ''}`, minor: true } : { icon: h(ChipIc, null), text: `${nm(e.pid)} −${-e.n} чипсин${e.src ? ` — ${e.src}` : ''}`, minor: true };
    case 'marketChip': return { icon: h(ChipIc, null), text: `на ${cn(e.cid)} ложится чипсина${e.n > 1 ? ` (на карте ${e.n})` : ''}`, cid: e.cid, minor: true };
    case 'attack': return { icon: '⚔', text: `${e.bespredel ? 'Беспредел' : nm(e.from)} атакует ${e.cid ? `${cn(e.cid)} ` : ''}${e.targets.map(nm).join(', ')}${e.unavoidable ? ' (неизбежно)' : ''}`, cid: e.cid, cls: 'ev-attack' };
    case 'defendPrompt': return null;
    case 'defend': return { icon: '🛡', text: `${nm(e.pid)} защищается картой ${cn(e.cid)}`, cid: e.cid, cls: 'ev-defend', sound: 'defend' };
    case 'redirect': return { icon: '↩', text: `атака перенаправлена на ${nm(e.to)}`, cls: 'ev-attack' };
    case 'damage': return { icon: '💥', text: `${nm(e.pid)} получает ${e.n} урона (❤ ${e.life})`, cls: 'ev-damage', sound: 'damage' };
    case 'heal': return { icon: '💚', text: `${nm(e.pid)} лечится на ${e.n} (❤ ${e.life})`, cls: 'ev-heal' };
    case 'life': return { icon: '❤', text: `${nm(e.pid)}: жизни ${e.delta > 0 ? '+' : ''}${e.delta} (❤ ${e.life})${e.src ? ` — ${e.src}` : ''}`, cls: e.delta > 0 ? 'ev-heal' : 'ev-damage' };
    case 'die': return { icon: '☠', text: `${nm(e.pid)} подох${e.killer != null ? ` — убийца ${nm(e.killer)}` : ''}`, cls: 'ev-die', sound: 'die' };
    case 'prize': return { icon: '🏆', text: `${nm(e.pid)} получает главный приз Крутагидона`, cls: 'ev-prize' };
    case 'ddk': {
      const t = DB.ddk[e.tid];
      return { icon: '💀', text: `${nm(e.pid)} получает жетон дохлого колдуна${e.from != null ? ` от ${nm(e.from)}` : ''} (${t ? ddkPenalty(t) : e.vp} ПО)${t && t.text ? `: ${t.text}` : ''}`, cls: 'ev-die', sound: 'die' };
    }
    case 'loser': return { icon: '🐴', text: e.on ? `${nm(e.pid)} становится лошарой` : `${nm(e.pid)} больше не лошара`, cls: 'ev-attack' };
    case 'bespredel': {
      const d = cardOrNull(e.cid);
      return { icon: '🔥', text: `${e.mega ? 'МЕГАБЕСПРЕДЕЛ' : 'БЕСПРЕДЕЛ'}: ${d ? d.text : ''}`, cid: e.cid, cls: 'ev-bespredel', sound: 'bespredel' };
    }
    case 'gain': {
      const where = e.where === 'hand' ? ' на руку' : e.where === 'deckTop' ? ' на верх колоды' : e.where === 'deckBottom' ? ' под низ колоды' : '';
      if (e.where === 'play') return { icon: '🎴', text: `${nm(e.pid)} вводит в игру`, cid: e.cid, cls: 'ev-buy' };
      if (e.back) return { icon: '↩', text: `${cn(e.cid)} возвращается владельцу — ${nm(e.pid)}`, cid: e.cid, minor: true };
      if (e.from != null) return { icon: '🎁', text: `${nm(e.from)} передаёт ${nm(e.pid)}${where}`, cid: e.cid, cls: 'ev-attack' };
      return { icon: '📥', text: `${nm(e.pid)} получает${where}`, cid: e.cid, cls: 'ev-buy' };
    }
    case 'move': return { icon: e.to === 'hand' ? '🂠' : '🂠', text: `${nm(e.pid)} ${e.to === 'hand' ? 'берёт на руку' : e.to === 'deckTop' ? 'кладёт на верх колоды' : 'замешивает в колоду'}: ${e.cids.map((c) => cardOrNull(c)?.name ?? '?').join(', ')}`, minor: true };
    case 'ddkLost': return e.to != null
      ? { icon: '💀', text: `${nm(e.pid)} отдаёт ${nm(e.to)} жетон дохлого колдуна${e.text ? `: ${e.text}` : ''}`, cls: 'ev-attack' }
      : { icon: '💀', text: `${nm(e.pid)} уничтожает жетон дохлого колдуна${e.text ? `: ${e.text}` : ''}`, cls: 'ev-heal' };
    case 'trash': return { icon: '🗑', text: `${e.pid != null ? `${nm(e.pid)} лишается ${cn(e.cid)} — уничтожена` : `уничтожена ${cn(e.cid)}`}`, cid: e.cid, minor: e.pid == null };
    case 'discard': return { icon: '♻', text: `${nm(e.pid)} сбрасывает ${e.cids.length > 6 ? `${e.cids.length} карт` : e.cids.map(cn).join(', ')}`, minor: true };
    case 'draw': return e.cids.length && (me == null || e.pid === me)
      ? { icon: '🂠', text: `${nm(e.pid)} берёт: ${e.cids.map((c) => cardOrNull(c)?.name ?? '?').join(', ')}`, minor: true }
      : { icon: '🂠', text: `${nm(e.pid)} берёт ${e.n} карт${e.reshuffled ? ' (сброс замешан)' : ''}`, minor: true };
    case 'endTurn': return { icon: '⏹', text: `${nm(e.pid)} заканчивает ход`, minor: true };
    case 'note': return { icon: 'ℹ', text: `${e.pid != null ? nm(e.pid) + ': ' : ''}${e.text}`, minor: true };
    case 'end': return { icon: '🏁', text: `Игра окончена${e.reason ? ` (${e.turn != null ? `в ход ${nm(e.turn)}: ` : ''}${e.reason})` : ''}!${e.winners ? ` Ничья — побеждают: ${e.winners.map(nm).join(', ')}` : ` Победитель: ${nm(e.winner)}`}`, cls: 'ev-prize' };
  }
}

export interface FeedItem extends EventDesc {
  seq: number;
  type?: GameEvent['type'];
  /** атака: вложенные исходы (защита, урон, смерть, жетоны, потерянные карты) */
  sub?: FeedItem[];
  /** участники события и цели атаки (служебное, для вложения исходов) */
  pids?: PlayerId[];
  targets?: PlayerId[];
  /** касается смотрящего (его бьют, у него забирают, ему передают) */
  mine?: boolean;
  /** текст без множителя и сколько одинаковых строк подряд в него сжато */
  base?: string;
  count?: number;
}

/** Кого касается событие */
function involved(e: GameEvent): PlayerId[] {
  const ids: (PlayerId | null | undefined)[] = [];
  if ('pid' in e) ids.push(e.pid);
  if (e.type === 'attack') ids.push(...e.targets);
  if (e.type === 'redirect') ids.push(e.to);
  if (e.type === 'ddkLost') ids.push(e.to);
  if (e.type === 'gain' && e.from != null) ids.push(e.from);
  if (e.type === 'end') ids.push(...(e.winners ?? [e.winner]));
  return ids.filter((x): x is PlayerId => x != null);
}

/** Слово «чипсин» с числом */
const chipsWord = (n: number): string => `${n > 0 ? '+' : ''}${n} чипсин`;

/**
 * Сжатая лента «что произошло»: только то, что меняет положение игроков — ходы, покупки, атаки и их исход
 * (защита, урон, смерть, жетоны, приз, лошара), беспределы, передачи и потери карт, чипсины (сумма за ход).
 * Розыгрыши, применения, доборы, сбросы своих карт и прочая механика — в полном журнале.
 */
export function digestEvents(G: Game, events: readonly GameEvent[], me: PlayerId | null = null): FeedItem[] {
  const out: FeedItem[] = [];
  const nm = (pid: PlayerId): string => mark(pid);
  let turn: PlayerId | null = null;
  // чипсины за ход: по игрокам, с источниками
  let chips = new Map<PlayerId, { n: number; src: Set<string>; seq: number }>();
  const flushChips = (): void => {
    for (const [pid, c] of chips) {
      if (!c.n) continue;
      const src = [...c.src].filter(Boolean);
      out.push({ seq: c.seq, icon: h(ChipIc, null), text: `${nm(pid)}: ${chipsWord(c.n)}${src.length ? ` (${src.join(', ')})` : ''}`, cls: c.n > 0 ? 'ev-heal' : 'ev-attack', mine: pid === me });
    }
    chips = new Map();
  };
  const feed = feedEvents(events);
  // последняя покупка: чипсины, лежавшие на купленной карте, дописываются в её строку
  let lastBuy: { pid: PlayerId; item: FeedItem } | null = null;
  for (let i = 0; i < feed.length; i++) {
    const e = feed[i];
    if (!e) continue;
    if (e.type === 'turn') {
      flushChips();
      turn = e.pid;
      lastBuy = null;
    }
    if (e.type === 'chips') {
      // оплата чипсинами уже видна в строке покупки («за 6 (2 чипсинами)»)
      if (e.src?.startsWith('покупка «')) continue;
      if (e.src?.startsWith('лежали на «') && lastBuy && lastBuy.pid === e.pid) {
        lastBuy.item.text += `, ${chipsWord(e.n)} с карты`;
        continue;
      }
      const c = chips.get(e.pid) ?? { n: 0, src: new Set<string>(), seq: e.seq };
      c.n += e.n;
      if (e.src) c.src.add(e.src);
      chips.set(e.pid, c);
      continue;
    }
    if (!important(e, turn)) continue;
    // сброс карты защиты + «защищается картой» — одно действие: строку сброса прячем
    if (e.type === 'discard' && feed.slice(i + 1, i + 3).some((x) => x?.type === 'defend' && x.pid === e.pid && e.iids.includes(x.iid))) continue;
    const d = describeEvent(G, e, me);
    if (!d) continue;
    // смерть всегда приносит жетон из стопки — «подох» и «получает жетон» сливаются в одну строку
    if (e.type === 'ddk' && e.from == null) {
      const prev = out[out.length - 1];
      const cut = d.text.indexOf('получает ');
      if (prev && prev.type === 'die' && prev.pids?.[0] === e.pid && cut >= 0) {
        prev.text += ` — ${d.text.slice(cut + 'получает '.length)}`;
        prev.base = prev.text;
        continue;
      }
    }
    const item: FeedItem = { ...d, minor: false, seq: e.seq, type: e.type, mine: me != null && involved(e).includes(me), pids: involved(e), targets: attackTargets(e) };
    // одинаковые строки подряд (две вялые палочки, два жетона) — одна с множителем
    const prev = out[out.length - 1];
    if (prev && prev.base === item.text && prev.cid === item.cid) {
      prev.count = (prev.count ?? 1) + 1;
      prev.text = `${prev.base} ×${prev.count}`;
      continue;
    }
    item.base = item.text;
    out.push(item);
    if (e.type === 'buy') lastBuy = { pid: e.pid, item };
  }
  flushChips();
  return nestAttacks(out.sort((a, b) => a.seq - b.seq));
}

/** Цели атаки (для вложения исходов) */
function attackTargets(e: GameEvent): PlayerId[] | undefined {
  return e.type === 'attack' ? [...e.targets, ...(e.from != null ? [e.from] : [])] : undefined;
}

/** Исходы, которые вкладываются в блок атаки, пока речь о её участниках */
const OUTCOME: ReadonlySet<GameEvent['type']> = new Set(['defend', 'redirect', 'damage', 'heal', 'life', 'die', 'ddk', 'ddkLost', 'prize', 'loser', 'gain', 'discard', 'trash', 'move', 'note']);

/**
 * Атака и её исходы — один блок: строка «кто кого атакует», под ней карта, ниже — защита, урон, смерть, жетоны,
 * полученные/потерянные карты участников. Блок закрывает любое событие не из исходов или не про участников.
 */
export function nestAttacks(items: FeedItem[]): FeedItem[] {
  const out: FeedItem[] = [];
  let atk: FeedItem | null = null;
  for (const it of items) {
    if (atk && it.type && OUTCOME.has(it.type) && it.pids?.some((p) => atk?.targets?.includes(p))) {
      if (it.type === 'redirect') for (const p of it.pids ?? []) if (!atk.targets?.includes(p)) atk.targets?.push(p);
      // карту атаки повторять в исходах незачем; другие карты (защита, переданная, полученная) — нужны
      (atk.sub ??= []).push(it.cid === atk.cid && it.type !== 'defend' ? { ...it, cid: null } : it);
      continue;
    }
    atk = it.type === 'attack' ? it : null;
    out.push(it);
  }
  return out;
}

/** Все события как строки ленты (полный журнал), с блоками атак */
export function allItems(G: Game, events: readonly GameEvent[], me: PlayerId | null = null): FeedItem[] {
  const items: FeedItem[] = [];
  for (const e of feedEvents(events)) {
    const d = describeEvent(G, e, me);
    if (!d) continue;
    items.push({ ...d, seq: e.seq, type: e.type, mine: me != null && involved(e).includes(me), pids: involved(e), targets: attackTargets(e) });
  }
  return nestAttacks(items);
}

/** Событие достойно сжатой ленты (turn — чей сейчас ход) */
function important(e: GameEvent, turn: PlayerId | null): boolean {
  switch (e.type) {
    case 'turn': case 'buy': case 'attack': case 'defend': case 'redirect': case 'damage': case 'heal': case 'life': case 'die':
    case 'prize': case 'ddk': case 'ddkLost': case 'loser': case 'bespredel': case 'end':
      return true;
    case 'gain': return !e.back;
    case 'trash': return e.pid != null;
    // сброс не в свой ход — по чужому эффекту (карты отняты)
    case 'discard': return e.pid !== turn && e.cids.length > 0;
    case 'play': return e.from != null;
    default: return false;
  }
}

/** События для ленты: «получает» вслед за «покупает» той же карты (между ними могут быть чипсины с карты) — дубль, прячем */
export function feedEvents(events: readonly GameEvent[]): GameEvent[] {
  const out: GameEvent[] = [];
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    if (!e) continue;
    if (e.type === 'gain' && events.slice(Math.max(0, i - 3), i).some((b) => b.type === 'buy' && b.iid === e.iid)) continue;
    out.push(e);
  }
  return out;
}
